import { useCallback, useEffect, useMemo, useState } from "react";
import { formatLanguageOptionLabel, parseSupportedTranslationLanguageTag, SUPPORTED_TRANSLATION_LANGUAGE_TAGS } from "../../../../shared/i18n/languageCatalog";
import { useI18n } from "../../../../shared/i18n/i18n";
import { formatFullDateForUiLanguage } from "../../../../shared/i18n/dateFormatting";
import { TranslationKey } from "../../../../shared/i18n/i18n";
import {
  archiveAdminTranslation,
  createAdminTranslation,
  getAdminTranslation,
  listAdminTranslations,
  unarchiveAdminTranslation,
  updateAdminTranslation,
} from "../../../../shared/requests";
import {
  AdminTranslationArchiveRequest,
  AdminOrganizationPermissions,
  AdminTranslationCreateRequest,
  AdminTranslationDetail,
  AdminTranslationDraft,
  AdminTranslationGetRequest,
  AdminTranslationListRequest,
  AdminTranslationUpdateRequest,
} from "../../../types/adminEntities";
import { useAdminOrganizationContextCache } from "../AdminOrganizationContextCache";
import { useAdminLoading } from "../AdminLoadingContext";
import { AdminNavSelection } from "../AdminNavList";
import { useAdminOrganizationContext } from "../useAdminOrganizationContext";
import { AdminBanner } from "../../common/AdminBanner";
import { AppPopup } from "../../../../shared/components/AppPopup";
import { AdminDetailActions } from "./AdminDetailActions";
import { AdminDetailSectionLayout } from "./AdminDetailSectionLayout";
import { AdminSelectField } from "./AdminSelectField";
import { AdminSingleDetailsTab } from "./AdminSingleDetailsTab";
import { AdminTextField } from "./AdminTextField";
import { AdminTranslationTextField } from "./AdminTranslationTextField";
import { DisabledActionReason, hasOrganizationPermission } from "../../../utils/adminPermissions";

type TranslationDetailSectionProps = {
  organizationId: null | string;
  isActive: boolean;
  isDesktopNavHidden: boolean;
  isMobileViewport: boolean;
  onCloseDesktopNav: () => void;
  onEntityMutated: () => void;
  onOpenDesktopNav: () => void;
  onSelectionChange: (selection: AdminNavSelection | null) => void;
  signId: null | string;
  siteId: null | string;
  t: (key: TranslationKey) => string;
  translationId: null | string;
};

type TranslationDetailMode = "create" | "edit" | "view";

const DEFAULT_TRANSLATION_DRAFT: AdminTranslationDraft = {
  content: "",
  language: "en",
};

const resolveTranslationSaveErrorMessage = (
  error: unknown,
  fallbackMessage: string,
  t: (key: TranslationKey) => string,
) => {
  if (typeof error === "object" && error !== null && "payload" in error) {
    const payload = error.payload;
    if (payload && typeof payload === "object" && "errorCode" in payload && payload.errorCode === "TRANSLATION_MAX_LENGTH_EXCEEDED") {
      return t("admin.translation.lengthExceededBanner");
    }
  }
  return error instanceof Error ? error.message || fallbackMessage : fallbackMessage;
};

export const TranslationDetailSection = ({
  organizationId,
  isActive,
  isDesktopNavHidden,
  isMobileViewport,
  onCloseDesktopNav,
  onEntityMutated,
  onOpenDesktopNav,
  onSelectionChange,
  signId,
  siteId,
  t,
  translationId,
}: TranslationDetailSectionProps) => {
  const { language } = useI18n();
  const { track } = useAdminLoading();
  const { ensureOrganizationContext } = useAdminOrganizationContextCache();
  const { organizationContext } = useAdminOrganizationContext(organizationId);
  const [organizationPermissions, setOrganizationPermissions] = useState<AdminOrganizationPermissions | null>(null);
  const [detail, setDetail] = useState<AdminTranslationDetail | null>(null);
  const [draft, setDraft] = useState<AdminTranslationDraft>(DEFAULT_TRANSLATION_DRAFT);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [existingTranslationLanguages, setExistingTranslationLanguages] = useState<string[]>([]);
  const [isLengthBannerDismissed, setIsLengthBannerDismissed] = useState(false);
  const [mode, setMode] = useState<TranslationDetailMode>("view");
  const [popup, setPopup] = useState<"archive" | "discard" | "unarchive" | null>(null);

  const allLanguageOptions = useMemo(() => {
    return SUPPORTED_TRANSLATION_LANGUAGE_TAGS.map((languageTag) => {
      return {
        label: formatLanguageOptionLabel(languageTag),
        value: languageTag,
      };
    });
  }, []);

  const languageOptions = useMemo(() => {
    if (mode !== "create") {
      return allLanguageOptions;
    }
    const existingLanguages = new Set(existingTranslationLanguages);
    return allLanguageOptions.filter((option) => !existingLanguages.has(option.value));
  }, [allLanguageOptions, existingTranslationLanguages, mode]);

  const loadDetail = useCallback(async (
    nextOrganizationId: string,
    nextSiteId: string,
    nextSignId: string,
    nextTranslationId: string,
  ) => {
    setErrorMessage(null);
    setIsBusy(true);
    setMode("view");
    try {
      const request: AdminTranslationGetRequest = {
        organizationId: nextOrganizationId,
        signId: nextSignId,
        siteId: nextSiteId,
        translationId: nextTranslationId,
      };
      const { payload } = await track(async () => getAdminTranslation(request));
      const normalizedLanguage = parseSupportedTranslationLanguageTag(payload.language) ?? DEFAULT_TRANSLATION_DRAFT.language;
      const normalizedDetail = { ...payload, language: normalizedLanguage };
      setDetail(normalizedDetail);
      setDraft({
        content: normalizedDetail.content ?? "",
        language: normalizedDetail.language,
      });
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.loadFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setDetail(null);
      setErrorMessage(message || fallbackMessage);
    } finally {
      setIsBusy(false);
    }
  }, [t, track]);

  const loadExistingTranslationLanguages = useCallback(async (
    nextOrganizationId: string,
    nextSiteId: string,
    nextSignId: string,
  ) => {
    try {
      const request: AdminTranslationListRequest = {
        organizationId: nextOrganizationId,
        includeArchived: true,
        limit: 1000,
        offset: 0,
        signId: nextSignId,
        siteId: nextSiteId,
        sortDirection: "ASC",
        sortField: "LANGUAGE",
      };
      const { payload } = await track(async () => listAdminTranslations(request));
      setExistingTranslationLanguages(
        (payload.items ?? [])
          .map((item) => parseSupportedTranslationLanguageTag(item.language))
          .filter((value): value is string => value !== null)
      );
    } catch {
      setExistingTranslationLanguages([]);
    }
  }, [track]);

  useEffect(() => {
    if (!isActive) {
      return;
    }
    if (!organizationId || !siteId || !signId || !translationId) {
      setDetail(null);
      setOrganizationPermissions(organizationContext?.organizationPermissions ?? null);
      setDraft(DEFAULT_TRANSLATION_DRAFT);
      setErrorMessage(null);
      setIsBusy(false);
      setExistingTranslationLanguages([]);
      setIsLengthBannerDismissed(false);
      setMode("create");
      setPopup(null);
      return;
    }
    setIsLengthBannerDismissed(false);
    void loadDetail(organizationId, siteId, signId, translationId);
  }, [organizationContext?.organizationPermissions, organizationId, isActive, loadDetail, signId, siteId, translationId]);

  useEffect(() => {
    if (!isActive || !organizationId || !siteId || !signId) {
      setExistingTranslationLanguages([]);
      return;
    }
    void loadExistingTranslationLanguages(organizationId, siteId, signId);
  }, [isActive, loadExistingTranslationLanguages, organizationId, signId, siteId]);

  useEffect(() => {
    setOrganizationPermissions(organizationContext?.organizationPermissions ?? null);
  }, [organizationContext]);

  useEffect(() => {
    if (mode !== "create") {
      return;
    }
    const currentLanguage = parseSupportedTranslationLanguageTag(draft.language);
    const hasCurrentLanguage = currentLanguage !== null && languageOptions.some((option) => option.value === currentLanguage);
    if (hasCurrentLanguage) {
      return;
    }
    setDraft((current) => ({
      ...current,
      language: languageOptions[0]?.value ?? "",
    }));
  }, [draft.language, languageOptions, mode]);

  const translationPermissionMissing = Boolean(organizationContext) && !hasOrganizationPermission(organizationPermissions, "TRANSLATION_CREATE");
  const translationQuotaReached = Boolean(organizationContext) && organizationContext.organizationUsage.activeTranslationCount >= organizationContext.activeSubscription.maxActiveTranslations;
  const createDisabledReason: DisabledActionReason = translationPermissionMissing
    ? "permission"
    : translationQuotaReached
      ? "subscription-limit"
      : null;
  const unarchiveDisabledReason: DisabledActionReason = translationPermissionMissing
    ? "permission"
    : translationQuotaReached
      ? "subscription-limit"
      : null;
  const canCreate = organizationId !== null && siteId !== null && signId !== null && hasOrganizationPermission(organizationPermissions, "TRANSLATION_CREATE") && !translationQuotaReached;
  const canUpdate = hasOrganizationPermission(organizationPermissions, "TRANSLATION_UPDATE");
  const canArchive = hasOrganizationPermission(organizationPermissions, "TRANSLATION_ARCHIVE");
  const canUnarchive = canArchive && !translationQuotaReached;
  const updateDisabledReason: DisabledActionReason = !hasOrganizationPermission(organizationPermissions, "TRANSLATION_UPDATE") ? "permission" : null;
  const archiveDisabledReason: DisabledActionReason = !hasOrganizationPermission(organizationPermissions, "TRANSLATION_ARCHIVE") ? "permission" : null;
  const hasAvailableCreateLanguage = languageOptions.length > 0;

  const handleCreate = () => {
    if (!organizationId || !siteId || !signId) {
      return;
    }
    onSelectionChange({ organizationId, create: true, signId, siteId, type: "translation" });
  };

  const handleEdit = () => {
    if (!detail || !canUpdate) {
      return;
    }
    setDraft({
      content: detail.content,
      language: detail.language,
    });
    setMode("edit");
  };

  const confirmDiscard = () => {
    if (!translationId) {
      onSelectionChange(null);
      return;
    }
    setMode("view");
    if (organizationId && siteId && signId) {
      void loadDetail(organizationId, siteId, signId, translationId);
    }
  };

  const confirmArchive = async () => {
    if (!detail || !organizationId || !siteId || !signId || !canArchive) {
      return;
    }
    setErrorMessage(null);
    setIsBusy(true);
    try {
      const request: AdminTranslationArchiveRequest = {
        organizationId,
        signId,
        siteId,
        translationId: detail.id,
      };
      await track(async () => archiveAdminTranslation(request));
      await ensureOrganizationContext(organizationId, { force: true });
      if (translationId) {
        await loadDetail(organizationId, siteId, signId, translationId);
      }
      onEntityMutated();
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.archiveFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setErrorMessage(message || fallbackMessage);
    } finally {
      setIsBusy(false);
    }
  };

  const confirmUnarchive = async () => {
    if (!detail || !organizationId || !siteId || !signId || !canUnarchive) {
      return;
    }
    setErrorMessage(null);
    setIsBusy(true);
    try {
      const request: AdminTranslationArchiveRequest = {
        organizationId,
        signId,
        siteId,
        translationId: detail.id,
      };
      await track(async () => unarchiveAdminTranslation(request));
      await ensureOrganizationContext(organizationId, { force: true });
      if (translationId) {
        await loadDetail(organizationId, siteId, signId, translationId);
      }
      onEntityMutated();
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.unarchiveFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setErrorMessage(message || fallbackMessage);
    } finally {
      setIsBusy(false);
    }
  };

  const handlePopupConfirm = async () => {
    const nextPopup = popup;
    setPopup(null);
    if (nextPopup === "discard") {
      confirmDiscard();
    } else if (nextPopup === "archive") {
      await confirmArchive();
    } else if (nextPopup === "unarchive") {
      await confirmUnarchive();
    }
  };

  const popupTitle = popup === "discard"
    ? t("admin.detail.confirm.discardTitle")
    : popup === "archive"
      ? t("admin.detail.confirm.archiveTitle")
      : popup === "unarchive"
        ? t("admin.detail.confirm.unarchiveTitle")
        : "";

  const popupBody = popup === "discard"
    ? t("admin.detail.confirm.discard")
    : popup === "archive"
      ? t("admin.detail.confirm.archive")
      : popup === "unarchive"
        ? t("admin.detail.confirm.unarchive")
        : "";

  const isReadOnly = mode === "view" || isBusy || (mode === "create" && !canCreate) || (mode === "edit" && !canUpdate);
  const shouldRenderPane = mode === "create" || detail !== null || errorMessage !== null;
  const translationLengthLimit = organizationContext?.activeSubscription.maxTranslationLength ?? null;
  const isTranslationLengthExceeded = translationLengthLimit !== null && draft.content.length > translationLengthLimit;
  const saveDisabledReason: DisabledActionReason = mode === "create"
    ? createDisabledReason ?? (!hasAvailableCreateLanguage || isTranslationLengthExceeded ? "data-problem" : null)
    : updateDisabledReason ?? (isTranslationLengthExceeded ? "data-problem" : null);
  const canSave = mode === "create"
    ? canCreate && hasAvailableCreateLanguage && !isTranslationLengthExceeded
    : canUpdate && !isTranslationLengthExceeded;

  useEffect(() => {
    if (!isTranslationLengthExceeded) {
      setIsLengthBannerDismissed(false);
    }
  }, [isTranslationLengthExceeded]);

  const handleSave = async () => {
    if (!organizationId || !siteId || !signId || !canSave) {
      return;
    }
    setErrorMessage(null);
    setIsBusy(true);
    try {
      if (mode === "create") {
        const request: AdminTranslationCreateRequest = {
          organizationId,
          content: draft.content,
          language: draft.language,
          signId,
          siteId,
        };
        const { payload } = await track(async () => createAdminTranslation(request));
        await ensureOrganizationContext(organizationId, { force: true });
        const createdId = payload.id;
        onEntityMutated();
        if (createdId) {
          onSelectionChange({ organizationId, signId, siteId, translationId: createdId, type: "translation" });
        }
      } else if (mode === "edit" && detail) {
        const request: AdminTranslationUpdateRequest = {
          organizationId,
          content: draft.content,
          language: draft.language,
          signId,
          siteId,
          translationId: detail.id,
        };
        await track(async () => updateAdminTranslation(request));
        await ensureOrganizationContext(organizationId, { force: true });
        setDetail({ ...detail, content: draft.content, language: draft.language });
        setMode("view");
        onEntityMutated();
      }
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.saveFailed");
      const message = resolveTranslationSaveErrorMessage(error, fallbackMessage, t);
      setErrorMessage(message || fallbackMessage);
    } finally {
      setIsBusy(false);
    }
  };

  if (!shouldRenderPane) {
    return null;
  }

  return (
    <>
      <AdminDetailSectionLayout
        actions={detail ? (
          <AdminDetailActions
            archiveDisabledReason={archiveDisabledReason}
            canArchive={canArchive}
            canUnarchive={canUnarchive}
            canCreate={canCreate}
            createActionLabel={t("admin.detail.action.createTranslation")}
            canUpdate={canUpdate}
            createDisabledReason={createDisabledReason}
            hasDetail={detail !== null}
            disableNonEditingActions={mode === "create" || mode === "edit"}
            isArchived={Boolean(detail?.archivedAt)}
            isBusy={isBusy}
            isDesktopNavHidden={isDesktopNavHidden}
            isEditing={false}
            isMobileViewport={isMobileViewport}
            onArchive={() => setPopup("archive")}
            onCloseDesktopNav={onCloseDesktopNav}
            onCreate={handleCreate}
            onDiscard={() => setPopup("discard")}
            onEdit={handleEdit}
            onOpenDesktopNav={onOpenDesktopNav}
            onSave={() => void handleSave()}
            onUnarchive={() => setPopup("unarchive")}
            showEditAction={false}
            updateDisabledReason={updateDisabledReason}
            t={t}
          />
        ) : null}
        errorMessage={errorMessage}
        eyebrow={t("admin.detail.eyebrow")}
        title={t("admin.detail.title.translation")}
      >
        {isTranslationLengthExceeded && !isLengthBannerDismissed ? (
          <AdminBanner
            color="red"
            onClose={() => setIsLengthBannerDismissed(true)}
          >
            {t("admin.translation.lengthExceededBanner")}
          </AdminBanner>
        ) : null}
        <AdminSingleDetailsTab
          ariaLabel={t("admin.translation.tabs.title")}
          panelLabel={t("admin.translation.tab.details")}
          tabIdPrefix="admin-translation"
        >
          <div className="admin-profile-section-header">
            <div className="admin-detail-actions admin-profile-section-actions">
              <AdminDetailActions
                canCreate={canCreate}
                canSave={canSave}
                canUpdate={mode === "create" ? canCreate : canUpdate}
                createDisabledReason={createDisabledReason}
                hasDetail={detail !== null}
                isArchived={Boolean(detail?.archivedAt)}
                isBusy={isBusy}
                isDesktopNavHidden={isDesktopNavHidden}
                isEditing={mode === "create" || mode === "edit"}
                isMobileViewport={isMobileViewport}
                onArchive={() => {}}
                onCloseDesktopNav={onCloseDesktopNav}
                onCreate={handleCreate}
                onDiscard={() => setPopup("discard")}
                onEdit={handleEdit}
                onOpenDesktopNav={onOpenDesktopNav}
                onSave={() => void handleSave()}
                onUnarchive={() => {}}
                saveDisabledReason={saveDisabledReason}
                showArchiveActions={false}
                showCreateAction={false}
                showNavToggle={false}
                updateDisabledReason={mode === "create" ? createDisabledReason : updateDisabledReason}
                t={t}
              />
            </div>
          </div>
          {detail || mode === "create" ? (
            <form className="admin-detail-form">
              <AdminSelectField
                isReadOnly={isReadOnly}
                label={t("admin.detail.field.language")}
                onChange={isReadOnly ? () => undefined : (value) => setDraft((current) => ({ ...current, language: value }))}
                options={languageOptions}
                t={t}
                value={draft.language}
                visibility="public"
              />
              <AdminTranslationTextField
                availableCharacterCount={translationLengthLimit ?? undefined}
                isReadOnly={isReadOnly}
                label={t("admin.detail.field.content")}
                onChange={(value) => setDraft((current) => ({ ...current, content: value }))}
                t={t}
                value={draft.content}
                visibility="public"
              />
              {detail?.archivedAt ? (
                <AdminTextField
                  isReadOnly
                  label={t("admin.detail.field.archivedAt")}
                  t={t}
                  value={formatFullDateForUiLanguage(detail.archivedAt, language)}
                  visibility="internal"
                />
              ) : null}
            </form>
          ) : null}
        </AdminSingleDetailsTab>
      </AdminDetailSectionLayout>
      <AppPopup
        actions={(
          <>
            <button
              className="admin-popup-action"
              disabled={isBusy}
              onClick={() => setPopup(null)}
              type="button"
            >
              {t("admin.popup.cancel")}
            </button>
            <button
              className="admin-popup-action primary"
              disabled={isBusy}
              onClick={() => void handlePopupConfirm()}
              type="button"
            >
              {t("admin.popup.confirm")}
            </button>
          </>
        )}
        isOpen={popup !== null}
        onClose={() => setPopup(null)}
        title={popupTitle}
      >
        <p>{popupBody}</p>
      </AppPopup>
    </>
  );
};
